import {
  Group,
  useGroupClient,
  useAsyncEffect,
  useSnackbarNotification,
} from '@jupiterone/jupiter-web-toolkit';
import React, { useCallback, createContext, useContext, useState } from 'react';

const FETCH_ALL_SIZE = 100;
const useGroupsLogic = () => {
  const notifications = useSnackbarNotification();
  const groupClient = useGroupClient();
  const [loading, setLoading] = useState<boolean>(true);
  const [groups, setGroups] = useState<Group[]>([]);
  const [nextGroupUrl, setNextGroupUrl] = useState<string | undefined>();

  useAsyncEffect(async () => {
    const {
      items,
      links: { next },
    } = await groupClient.getGroups(undefined, FETCH_ALL_SIZE);
    setGroups(items);
    setNextGroupUrl(next);
    setLoading(false);
  }, []);

  // hook internal
  const fetchNextGroups = async (oldNext: string) => {
    const {
      items,
      links: { next },
    } = await groupClient.getGroups(oldNext);
    setGroups((oldGroups) => [...oldGroups, ...items]);
    return next;
  };

  // Passed to children props: useCallback optimizes
  const fetchMoreGroups = useCallback(async () => {
    if (loading || !nextGroupUrl) return;
    setLoading(true);
    setNextGroupUrl(await fetchNextGroups(nextGroupUrl));
    setLoading(false);
  }, [loading, nextGroupUrl]);

  // Passed to children props: useCallback optimizes
  const fetchAllGroups = useCallback(async () => {
    if (loading || !nextGroupUrl) return;
    setLoading(true);
    const _fetchNextGroups = async (next?: string): Promise<string> => {
      if (!next) return Promise.resolve('');
      return await _fetchNextGroups(await fetchNextGroups(next));
    };
    await _fetchNextGroups(nextGroupUrl);
    setNextGroupUrl(undefined);
    setLoading(false);
  }, [loading, nextGroupUrl]);

  const createGroup = async () => {
    const group = await groupClient.createGroup();
    const newGroups = [...groups, group];
    newGroups.sort((a, b) => {
      if (a.name === b.name) return 0;
      return a.name > b.name ? 1 : -1;
    });
    setGroups(newGroups);
    return group;
  };

  const updateGroupLocally = async (group: Group) => {
    const newGroups = [...groups];
    const index = newGroups.findIndex(({ id }) => id === group.id);
    if (index === -1) return;
    newGroups[index] = group;
    setGroups(newGroups);
  };

  const saveGroup = async (group: Group) => {
    const newGroup = await groupClient.updateGroup(group);
    await updateGroupLocally(newGroup);
  };

  const deleteGroup = async (idToDelete: string) => {
    await groupClient.deleteGroup(idToDelete);
    const newGroups = groups.filter(({ id }) => id === idToDelete);
    setGroups(newGroups);
    notifications.success('Group Deleted');
  };

  return {
    groups,
    loading,
    hasMore: Boolean(nextGroupUrl),
    fetchMoreGroups,
    fetchAllGroups,
    createGroup,
    updateGroupLocally,
    saveGroup,
    deleteGroup,
  };
};

type UseGroups = ReturnType<typeof useGroupsLogic>;
const GroupContext = createContext({} as UseGroups);
const useGroups = () => useContext(GroupContext);
export default useGroups;

interface UseGroupContextProviderProps {
  children: React.ReactNode;
}

export const GroupsProvider = ({ children }: UseGroupContextProviderProps) => {
  const groups = useGroupsLogic();
  return (
    <GroupContext.Provider value={groups}>{children}</GroupContext.Provider>
  );
};
